import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Content, Lesson } from '../../../../../../types';
import SectionContent from '../section-content/SectionContent';
import { useEditUnit } from '../../../edit/useEditUnit';
import { useMoveModuleMutation, useHeaMoveModuleMutation, LessonType } from '@api/graphql-codegen';
import {
  DropResult,
  ResponderProvided,
  DraggableProvided,
  DroppableProvided,
  DraggableStateSnapshot
} from '@src/utils/componentCasts';
import { DragDropContext, Droppable, Draggable } from '@src/utils/componentCasts';

type Props = {
  contents: Content[];
  lesson?: Lesson;
  isHEPreparation?: boolean;
  lessonType: LessonType | undefined;
  isSupportingMaterial?: boolean;
};

const reorder = (list: any, startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

function SectionContents(props: Props) {
  const { contents, lesson, isHEPreparation, lessonType, isSupportingMaterial } = props;
  const [ localContents, setLocalContents ] = useState<Array<Content>>([]);
  const { content: selectedContent, unit, heaUnit } = useEditUnit();
  const [moveModule] = useMoveModuleMutation();
  const [moveHeModule] = useHeaMoveModuleMutation();

  useEffect(() => {
    setLocalContents(contents);
  }, [contents])

  const handleDragEnd = useCallback(async (result: DropResult, provided?: ResponderProvided) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    const reorderedSubItems = reorder(
      localContents,
      sourceIndex,
      destIndex
    );

    const selectedModule = (reorderedSubItems as Content[]).find((content: Content, index) => index === destIndex);
    setLocalContents(reorderedSubItems as Content[]);
    if (isHEPreparation) {
      if (heaUnit) {
        await moveHeModule({
          variables: {
            input: {
              moduleId: selectedModule!.id,
              oldPosition: {
                lessonId: undefined,
                unitId: heaUnit.id,
                position: result.source!.index + 1
              },
              newPosition: {
                lessonId: undefined,
                unitId: heaUnit.id,
                position: result.destination!.index + 1
              }
            }
          }
        })
      }
    } else {
      await moveModule({
        variables: {
          input: {
            moduleId: selectedModule!.id,
            oldPosition: {
              lessonId: undefined,
              unitId: unit!.id,
              position: result.source!.index + 1
            },
            newPosition: {
              lessonId: undefined,
              unitId: unit!.id,
              position: result.destination!.index + 1
            },
            alternate: lessonType === LessonType.ALTERNATE ? true : undefined,
          }
        }
      });
    }
  }, [localContents, isHEPreparation, heaUnit, moveHeModule, moveModule, unit, lessonType]);

  const modules = useMemo(() => [...localContents || []], [localContents])

  return (
    // @ts-ignore
    <DragDropContext onDragEnd={handleDragEnd}>
      // @ts-ignore
      <Droppable droppableId="droppableContent" type="teacherModuleItem">
        {(droppableProvided: DroppableProvided) => (
          <React.Fragment>
            <div
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
            >
              {modules.map((item: Content, index: number) => (
                <Draggable
                  key={item.id}
                  draggableId={item.id}
                  index={index}
                >
                  {(
                    draggableProvided: DraggableProvided,
                    snapshot: DraggableStateSnapshot
                  ) => {
                    const sectionSelected = selectedContent?.id === item.id;
                    return (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                      >
                        <SectionContent draggableProvided={draggableProvided} snapshot={snapshot} lesson={lesson} key={item.id} selected={sectionSelected} content={item} isPreparation isSupportingMaterial={isSupportingMaterial}/>
                      </div>
                    );
                  }}
                </Draggable>
              ))}
              {droppableProvided.placeholder as any}
            </div>
          </React.Fragment>
        )}
      </Droppable>
  </DragDropContext>
  );
}

export default SectionContents;